// src/redux/slices/assessmentScheduleSlice.jsx
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "../services/endpoint";

// Get assessment schedules list
export const getAssessmentSchedules = createAsyncThunk(
  "assessmentSchedule/getAssessmentSchedules",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      // Build URL with query parameters
      let url = `${BASE_URL}/api/assessment-schedule/get-list`;
      const queryParams = new URLSearchParams();

      // Add date parameters if they exist
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "Failed to fetch assessment schedules");
      }

      return data;
    } catch (err) {
      return rejectWithValue("Network error");
    }
  },
);

// Update an assessment schedule
export const updateAssessmentSchedule = createAsyncThunk(
  "assessmentSchedule/updateAssessmentSchedule",
  async (scheduleData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      console.log("=== DEBUG UPDATE ASSESSMENT SCHEDULE ===");
      console.log("Schedule data:", scheduleData);

      const response = await fetch(`${BASE_URL}/api/assessment-schedule/update`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(scheduleData),
      });

      const data = await response.json();
      console.log("Update assessment schedule response:", data);

      if (!response.ok || !data.success) {
        return rejectWithValue(data.message || "Failed to update assessment schedule");
      }

      return data;
    } catch (err) {
      console.error("Update assessment schedule error:", err);
      return rejectWithValue("Network error");
    }
  },
);

// Delete an assessment schedule
export const deleteAssessmentSchedule = createAsyncThunk(
  "assessmentSchedule/deleteAssessmentSchedule",
  async (scheduleId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${BASE_URL}/api/assessment-schedule/delete/${scheduleId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return rejectWithValue(data.message || "Failed to delete assessment schedule");
      }

      return data;
    } catch (err) {
      return rejectWithValue("Network error");
    }
  },
);

const assessmentScheduleSlice = createSlice({
  name: "assessmentSchedule",
  initialState: {
    schedules: [],
    isLoading: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    updateError: null,
    deleteError: null,
    successMessage: "",
  },
  reducers: {
    clearAssessmentScheduleState: (state) => {
      state.error = null;
      state.updateError = null;
      state.deleteError = null;
      state.successMessage = "";
    },
    clearSchedules: (state) => {
      state.schedules = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get Assessment Schedules
    builder
      .addCase(getAssessmentSchedules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssessmentSchedules.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success) {
          state.schedules = action.payload.data || [];
        } else {
          state.error = action.payload.message || "Failed to fetch assessment schedules";
        }
      })
      .addCase(getAssessmentSchedules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch assessment schedules";
      });

    // Update Assessment Schedule
    builder
      .addCase(updateAssessmentSchedule.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.successMessage = "";
      })
      .addCase(updateAssessmentSchedule.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload.success) {
          state.successMessage = action.payload.message || "Assessment schedule updated successfully";
          
          // Update the schedule in the list if it exists
          if (action.payload.data && action.payload.data.id) {
            const index = state.schedules.findIndex(
              (schedule) => schedule.id === action.payload.data.id
            );
            if (index !== -1) {
              state.schedules[index] = {
                ...state.schedules[index],
                ...action.payload.data,
              };
            }
          }
        } else {
          state.updateError = action.payload.message || "Failed to update assessment schedule";
        }
      })
      .addCase(updateAssessmentSchedule.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
      });

    // Delete Assessment Schedule
    builder
      .addCase(deleteAssessmentSchedule.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
        state.successMessage = "";
      })
      .addCase(deleteAssessmentSchedule.fulfilled, (state, action) => {
        state.isDeleting = false;
        
        // Get the schedule id from the meta arg (the id passed to the thunk)
        const scheduleId = action.meta.arg;
        
        if (action.payload.success) {
          state.successMessage = action.payload.message || "Assessment schedule deleted successfully";
          // Remove the deleted schedule from the list
          state.schedules = state.schedules.filter(
            (schedule) => schedule.id !== scheduleId
          );
        } else {
          state.deleteError = action.payload.message || "Failed to delete assessment schedule";
        }
      })
      .addCase(deleteAssessmentSchedule.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload;
      });
  },
});

export const { clearAssessmentScheduleState, clearSchedules } = assessmentScheduleSlice.actions;
export default assessmentScheduleSlice.reducer;